import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "/icon-v2-512.png";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setVerifying(false);
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/verify-reset-token/${token}`);
      setTokenValid(true);
      setEmail(response.data.email);
    } catch (error) {
      setTokenValid(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="auth-container px-4" style={{ background: 'linear-gradient(180deg, #0B1F3A 0%, #1a3a8f 50%, #F2F5F7 50%)' }}>
        <div className="w-full max-w-md text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
          <p className="text-white mt-4">Verifying reset token...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container px-4" style={{ background: 'linear-gradient(180deg, #0B1F3A 0%, #1a3a8f 50%, #F2F5F7 50%)' }}>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src={LOGO_URL} 
            alt="AegisPay Logo" 
            className="w-20 h-20 mx-auto mb-4 rounded-2xl object-cover shadow-lg"
          />
          <h1 className="text-3xl font-bold text-white">Aegis<span className="text-[#1E5EFF]">Pay</span></h1>
          <p className="text-blue-200 mt-1">Save smarter with group savings</p>
          <p className="text-blue-200/70 italic text-[11px] mt-1">Your money follows rules, not promises</p>
        </div>

        <Card className="glass-card border-0 shadow-xl">
          {!token || !tokenValid ? (
            <>
              <CardHeader className="text-center pb-2">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mx-auto mb-4">
                  <AlertCircle className="w-6 h-6 text-red-500" />
                </div>
                <CardTitle className="text-xl text-[#0B1F3A]">Invalid Reset Link</CardTitle>
                <CardDescription>
                  This password reset link is invalid or has expired.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={() => navigate('/forgot-password')}
                  className="w-full h-12 rounded-full bg-[#0B1F3A] hover:bg-[#1a3a8f] text-white"
                >
                  Request New Link
                </Button>
                <div className="mt-4 text-center">
                  <Link to="/login" className="text-sm text-[#2272C5] font-medium hover:underline">
                    Back to Login
                  </Link>
                </div>
              </CardContent>
            </>
          ) : success ? (
            <>
              <CardHeader className="text-center pb-2">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-100 mx-auto mb-4">
                  <CheckCircle className="w-6 h-6 text-emerald-500" />
                </div>
                <CardTitle className="text-xl text-[#0B1F3A]">Password Reset!</CardTitle>
                <CardDescription>
                  Your password has been successfully changed.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={() => navigate('/login')}
                  className="w-full h-12 rounded-full bg-[#0B1F3A] hover:bg-[#1a3a8f] text-white"
                  data-testid="login-after-reset"
                >
                  Continue to Login
                </Button>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-xl text-[#0B1F3A]">Reset Password</CardTitle>
                <CardDescription>
                  Enter a new password for {email}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">New Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="At least 6 characters"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="h-12 rounded-xl bg-slate-50 pr-10"
                        data-testid="reset-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="Re-enter your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="h-12 rounded-xl bg-slate-50"
                      data-testid="reset-confirm-password-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 rounded-full bg-[#0B1F3A] hover:bg-[#1a3a8f] text-white font-medium btn-press"
                    disabled={loading}
                    data-testid="reset-submit-button"
                  >
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </form>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
